/* Plantica — a rail: a row of cards that scrolls sideways

   Shared. The experts row uses it, and so does the video row on the product
   page; any future row of cards should too, rather than growing a third copy of
   this to drift out of step with the other two.

   The row scrolls natively: `overflow-x` and `scroll-snap` in each section's
   own stylesheet do the work, a finger or a trackpad moves it, and with this
   file blocked the row is still a row that can be swiped and read. Nothing here
   is required for the content to exist.

   What it adds is the two arrows, for the desktop where there is nothing to
   swipe with. They start hidden and appear only once the row actually overflows
   — which depends on the window as much as on how many cards there are, so it
   cannot be settled in Liquid. A button that scrolls nothing is worse than no
   button.

   A step is one card plus one gap, read off the card rather than assumed here:
   how many cards fit is a number each section sets in CSS, and this file should
   not have to know it.

   The other thing it adds is dragging. A finger scrolls an overflowing box and
   so does a trackpad, but a mouse held down and moved sideways does nothing at
   all — the row looks draggable and is not, which is worse than looking
   fixed. So the pointer is followed by hand, for mice and pens only: touch is
   left to the browser, which does it better than this could, with momentum.

   The markup contract is four data attributes, so no section has to name its
   classes the way another one does:

     <plantica-rail>
       [data-rail-arrows]           the pair of buttons; gets `is-on` when the
                                    row overflows
         [data-rail-prev] [data-rail-next]
       [data-rail-viewport]         the box that scrolls; gets `is-draggable`
                                    and, while a mouse is dragging it,
                                    `is-dragging`
         [data-rail-cell] …         one per card, for measuring and snapping

   Custom file. See CUSTOMIZATIONS.md. */

(() => {
  if (customElements.get('plantica-rail')) return;

  // Rounding in the layout leaves a pixel or two of phantom overflow.
  const SLACK = 4;
  // Far enough that a click with an unsteady hand is still a click.
  const DRAG_FROM = 5;
  // Fallback for browsers without `scrollend`, to put snapping back after the
  // rail has settled.
  const SETTLED_AFTER = 450;

  class PlanticaRail extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('[data-rail-viewport]');
      if (!this.viewport) return;

      this.arrows = this.querySelector('[data-rail-arrows]');
      this.prev = this.querySelector('[data-rail-prev]');
      this.next = this.querySelector('[data-rail-next]');

      this.update = this.update.bind(this);
      this.queue = () => {
        if (this.pending) return;
        this.pending = requestAnimationFrame(() => {
          this.pending = 0;
          this.update();
        });
      };

      if (this.prev) this.prev.addEventListener('click', () => this.move(-1));
      if (this.next) this.next.addEventListener('click', () => this.move(1));

      this.viewport.addEventListener('scroll', this.queue, { passive: true });
      this.drag();

      if ('ResizeObserver' in window) {
        this.observer = new ResizeObserver(this.queue);
        this.observer.observe(this.viewport);
      } else {
        window.addEventListener('resize', this.queue);
      }

      this.update();
      // Type and photographs both land late and both change the width.
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(this.update);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      window.removeEventListener('resize', this.queue);
    }

    /* Following the pointer means writing `scrollLeft` on every move, and with
       `scroll-snap-type: x mandatory` every one of those writes springs back to
       the nearest card — the row would shudder and go nowhere. So snapping is
       switched off for the length of the drag and put back once the rail has
       settled where it was let go. */
    drag() {
      const rail = this.viewport;
      let following = false;
      let dragging = false;
      let pointer = null;
      let fromX = 0;
      let fromLeft = 0;

      rail.addEventListener('pointerdown', (event) => {
        // Touch already scrolls this, and with momentum we cannot match.
        if (event.pointerType === 'touch' || event.button !== 0) return;
        following = true;
        dragging = false;
        pointer = event.pointerId;
        fromX = event.clientX;
        fromLeft = rail.scrollLeft;
      });

      rail.addEventListener('pointermove', (event) => {
        if (!following || event.pointerId !== pointer) return;

        const moved = event.clientX - fromX;
        if (!dragging) {
          if (Math.abs(moved) < DRAG_FROM) return;
          dragging = true;
          rail.classList.add('is-dragging');
          if (rail.setPointerCapture) rail.setPointerCapture(pointer);
        }

        rail.scrollLeft = fromLeft - moved;
        event.preventDefault();
      });

      const release = () => {
        if (!following) return;
        following = false;
        if (rail.hasPointerCapture && rail.hasPointerCapture(pointer)) {
          rail.releasePointerCapture(pointer);
        }
        if (!dragging) return;
        dragging = false;

        // The card the drag ended on, not the pixel it ended on.
        this.settle();

        // A drag that ends on a card would otherwise follow that card's link,
        // or open its video.
        this.swallow = true;
        window.setTimeout(() => {
          this.swallow = false;
        }, 0);
      };

      rail.addEventListener('pointerup', release);
      rail.addEventListener('pointercancel', release);
      rail.addEventListener('lostpointercapture', release);

      // Photographs and links are draggable in their own right; that drag is
      // not the one being asked for here.
      rail.addEventListener('dragstart', (event) => {
        if (following) event.preventDefault();
      });

      rail.addEventListener(
        'click',
        (event) => {
          if (!this.swallow) return;
          event.preventDefault();
          event.stopPropagation();
          this.swallow = false;
        },
        true
      );
    }

    /* Glide to the nearest card and hand snapping back once we are there.
       Measured from the boxes on screen rather than `offsetLeft`, which is
       relative to whichever ancestor happens to be positioned. */
    settle() {
      const rail = this.viewport;
      const cells = Array.from(rail.querySelectorAll('[data-rail-cell]'));
      if (!cells.length) {
        rail.classList.remove('is-dragging');
        return;
      }

      const edge = rail.getBoundingClientRect().left;
      const stops = cells.map((cell) => rail.scrollLeft + (cell.getBoundingClientRect().left - edge));

      // The end of the track is a resting place too, and on a rail whose last
      // card peeks by less than its own width it is the important one: the
      // nearest card start from anywhere in that final stretch is the one
      // behind you. Without this, a drag to the end slides straight back.
      stops.push(rail.scrollWidth - rail.clientWidth);

      let best = rail.scrollLeft;
      let closest = Infinity;

      stops.forEach((at) => {
        const gap = Math.abs(at - rail.scrollLeft);
        if (gap < closest) {
          closest = gap;
          best = at;
        }
      });

      const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      rail.scrollTo({ left: best, behavior: smooth ? 'smooth' : 'auto' });

      const restore = () => rail.classList.remove('is-dragging');
      if ('onscrollend' in window) {
        rail.addEventListener('scrollend', restore, { once: true });
        // Never arrives if the rail was already there and nothing scrolled.
        window.setTimeout(restore, SETTLED_AFTER);
      } else {
        window.setTimeout(restore, SETTLED_AFTER);
      }
    }

    step() {
      const cell = this.viewport.querySelector('[data-rail-cell]');
      if (!cell) return this.viewport.clientWidth;

      const gap = parseFloat(getComputedStyle(cell.parentElement).columnGap) || 0;
      return cell.getBoundingClientRect().width + gap;
    }

    move(direction) {
      const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.viewport.scrollBy({
        left: this.step() * direction,
        behavior: smooth ? 'smooth' : 'auto',
      });
    }

    update() {
      const { scrollLeft, scrollWidth, clientWidth } = this.viewport;
      const overflows = scrollWidth - clientWidth > SLACK;

      if (this.arrows) this.arrows.classList.toggle('is-on', overflows);
      // Nothing to drag when the row already fits, so no hand offering to.
      this.viewport.classList.toggle('is-draggable', overflows);

      // `swiper-button-disabled` is the theme's own class for a spent arrow, so
      // ours dims exactly like every other slider in the shop. The attribute is
      // there as well: the class is only a look, and a button that cannot do
      // anything should not take a tab stop either.
      this.spend(this.prev, !overflows || scrollLeft <= SLACK);
      this.spend(this.next, !overflows || scrollLeft + clientWidth >= scrollWidth - SLACK);
    }

    spend(button, done) {
      if (!button) return;
      button.classList.toggle('swiper-button-disabled', done);
      button.disabled = done;
    }
  }

  customElements.define('plantica-rail', PlanticaRail);
})();
